'use client';

import {
  Box,
  Text,
  HStack,
  VStack,
  Badge,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Spinner,
  Divider,
} from '@chakra-ui/react';
import type { RecentNotification } from './useSendMessageModalData';

function getChannelBadge(notificationChannels: string[]) {
  if (
    notificationChannels.includes('sms') &&
    notificationChannels.includes('email')
  ) {
    return (
      <Badge colorScheme="purple" fontSize="xs">
        SMS + E-post
      </Badge>
    );
  }
  if (notificationChannels.includes('sms')) {
    return (
      <Badge colorScheme="blue" fontSize="xs">
        SMS
      </Badge>
    );
  }
  return (
    <Badge colorScheme="teal" fontSize="xs">
      E-post
    </Badge>
  );
}

interface NotificationHistoryProps {
  recentNotifications: RecentNotification[];
  isLoadingHistory: boolean;
}

export function NotificationHistory({
  recentNotifications,
  isLoadingHistory,
}: NotificationHistoryProps) {
  if (recentNotifications.length === 0) return null;

  return (
    <Box>
      {isLoadingHistory ? (
        <HStack justify="center" py={4}>
          <Spinner size="sm" />
          <Text fontSize="sm" color="gray.500">
            Laddar historik...
          </Text>
        </HStack>
      ) : (
        <Accordion allowMultiple>
          {recentNotifications.map((notification) => (
            <AccordionItem key={notification.id} border="none" mb={2}>
              <AccordionButton
                p={3}
                borderWidth="1px"
                borderRadius="md"
                bg="gray.50"
                _hover={{ bg: 'gray.100' }}
                _expanded={{ bg: 'gray.100', borderBottomRadius: 0 }}
              >
                <HStack flex="1" justify="space-between" align="center">
                  <VStack align="start" spacing={0} flex="1">
                    <Text fontWeight="medium" fontSize="sm" noOfLines={1}>
                      {notification.title}
                    </Text>
                    <HStack fontSize="xs" color="gray.600" spacing={2}>
                      <Text>{notification.recipient_count} spekulanter</Text>
                      <Text>•</Text>
                      <Text>
                        {new Date(
                          notification.sent_at || notification.created_at
                        ).toLocaleDateString('sv-SE', {
                          month: 'short',
                          day: 'numeric',
                        })}
                      </Text>
                    </HStack>
                  </VStack>
                  <HStack spacing={2}>
                    {getChannelBadge(notification.channels)}
                    <Badge
                      colorScheme={
                        notification.is_sent ? 'green' : 'orange'
                      }
                      fontSize="xs"
                    >
                      {notification.is_sent ? 'Skickat' : 'Väntar'}
                    </Badge>
                    <AccordionIcon />
                  </HStack>
                </HStack>
              </AccordionButton>
              <AccordionPanel
                pb={3}
                px={3}
                pt={2}
                borderWidth="1px"
                borderTop="none"
                borderBottomRadius="md"
                bg="white"
              >
                <VStack align="stretch" spacing={2}>
                  <Box>
                    <Text
                      fontSize="xs"
                      fontWeight="medium"
                      color="gray.700"
                      mb={1}
                    >
                      Meddelande:
                    </Text>
                    <Text fontSize="sm" color="gray.600">
                      {notification.message}
                    </Text>
                  </Box>
                  <HStack fontSize="xs" color="gray.500" spacing={3}>
                    {notification.sender && (
                      <>
                        <Text fontWeight="medium">
                          Skickat av: {notification.sender.first_name}{' '}
                          {notification.sender.last_name}
                        </Text>
                        <Text>•</Text>
                      </>
                    )}
                    <Text>
                      {new Date(
                        notification.sent_at || notification.created_at
                      ).toLocaleDateString('sv-SE', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </Text>
                  </HStack>
                </VStack>
              </AccordionPanel>
            </AccordionItem>
          ))}
        </Accordion>
      )}
      <Divider my={4} />
    </Box>
  );
}
